import {
  AwxLinkedAccountStatus,
  useAchPlaidAuthLinkTokenQuery,
  useConnectLinkedBankAccountMutation,
} from "@common/utils/graphql";
import { useEffect, useRef, useState } from "react";
import { PlaidLink } from "react-plaid-link";
import {
  PlaidSuccessResponse,
  plaidSuccessResponseValidator,
} from "./PlaidSuccessMetadata.model";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import { NoResults } from "@common/components";
import { Body, Heading } from "@hypercard/web-components";
import { Bank } from "@phosphor-icons/react";
import { useToast } from "@/components/ui/use-toast";
import { LinkedAccountMandateDialog } from "./LinkedAccountMandateDialog";
import { useUpdateLinkedAccountStatus } from "../hooks";

type Props = {
  linkedAccountStatus?: AwxLinkedAccountStatus | null;
};
const ConnectedAccountLink = ({ linkedAccountStatus }: Props) => {
  const { toast } = useToast();
  const updateLinkedkedAccountStatus = useUpdateLinkedAccountStatus();
  const [isLoading, setIsLoading] = useState(false);

  const [plaidResponse, setPlaidResponse] = useState<PlaidSuccessResponse>();

  const triggerMandateDialogRef = useRef<HTMLButtonElement | null>(null);

  const { data, loading } = useAchPlaidAuthLinkTokenQuery();

  useEffect(() => {
    if (linkedAccountStatus === AwxLinkedAccountStatus.Failed) {
      toast({
        title: "Failed to link bank account, please try again",
        variant: "warning",
      });
    } else if (linkedAccountStatus === AwxLinkedAccountStatus.RequiresAction) {
      toast({
        title: "Please relink your bank account",
        variant: "warning",
      });
    }
  }, [linkedAccountStatus, toast]);

  // Set link token once the data is ready
  const linkToken =
    data?.achAuthLinkToken?.__typename === "QueryAchAuthLinkTokenSuccess"
      ? data.achAuthLinkToken.data
      : "";

  // Use the mutation hook
  const [createLinkedAccount] = useConnectLinkedBankAccountMutation();

  const onSuccessPlaidLink = async (public_token: string, metadata: any) => {
    console.log(
      `PLAID: onSuccess - ${public_token} - ${JSON.stringify(metadata)}`,
    );
    const parsedData = plaidSuccessResponseValidator.parse({
      public_token,
      metadata,
    } satisfies PlaidSuccessResponse);
    setPlaidResponse(parsedData);
    triggerMandateDialogRef.current?.click();
  };

  const onEventPlaidLink = (eventName: string, metadata: any) => {
    console.log(`PLAID: onEvent - ${eventName} - ${JSON.stringify(metadata)}`);
  };

  const onExitPlaidLink = (err: any, metadata: any) => {
    console.log(
      `PLAID: onExit - ${JSON.stringify(err)} - ${JSON.stringify(metadata)}`,
    );
    setPlaidResponse(undefined);
  };

  const onSubmitMandateSignatoryEmail = async (
    mandateSignatoryEmail: string,
  ) => {
    if (!plaidResponse) {
      return;
    }

    const { public_token, metadata } = plaidResponse;
    setIsLoading(true);

    try {
      const { data } = await createLinkedAccount({
        variables: {
          publicToken: public_token,
          institutionId: metadata.institution.institution_id,
          institutionName: metadata.institution.name,
          accountId: metadata.account.id,
          accountName: metadata.account.name,
          accountMask: metadata.account.mask,
          signatoryEmail: mandateSignatoryEmail,
        },
      });
      if (
        data?.awxConnectLinkedAccount.__typename !==
        "MutationAwxConnectLinkedAccountSuccess"
      ) {
        throw new Error();
      }
      setIsLoading(false);
      updateLinkedkedAccountStatus(AwxLinkedAccountStatus.Processing);
      console.log(`Linked account created: ${JSON.stringify(data)}`);
    } catch (error) {
      setIsLoading(false);
      toast({
        title: "Failed to integrate bank account, please try again",
        variant: "destructive",
      });
      console.log(`Error creating linked account: ${JSON.stringify(error)}`);
    }
  };

  const buttonClasses =
    "ml-28 inline-flex items-center justify-center whitespace-nowrap rounded-sm text-sm font-medium ring-offset-background transition-colors bg-primary text-primary-foreground hover:bg-primary/90 w-[200px] h-12 px-4 py-2";

  if (loading || isLoading) return <FullPageLoader />;

  return (
    <div className="ml-4 mr-4 mt-6 flex flex-grow flex-col">
      <div className="min-w-screen flex flex-row justify-between">
        <div className="start-0 flex flex-col items-start">
          <Heading size="lg" weight="regular" className="mb-3">
            Company Bank Account
          </Heading>
          <Body size="sm" weight="regular" className="text-base text-slate">
            Integrate the company bank account for employee reimbursements. All
            approved reimbursement requests will be withdrawn from this account
            and deposited into the employee's personal bank account. Please
            contact Hypercard support to cancel or reverse any transfers.
          </Body>
        </div>
        <PlaidLink
          className={buttonClasses}
          style={{
            textDecoration: "none",
          }}
          token={linkToken}
          onSuccess={onSuccessPlaidLink}
          onEvent={onEventPlaidLink}
          onExit={onExitPlaidLink}
        >
          Integrate Bank Account
        </PlaidLink>
      </div>
      <NoResults
        Icon={Bank}
        header="No bank accounts added"
        body="Integrate your bank account"
      />
      <LinkedAccountMandateDialog
        triggerButtonRef={triggerMandateDialogRef}
        isLoading={isLoading}
        plaidResponse={plaidResponse}
        onConfirm={onSubmitMandateSignatoryEmail}
      />
    </div>
  );
};

export default ConnectedAccountLink;
